import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { isAccessError } from './monitoring-api';
import { emptyTranscript, updateTranscript } from './monitoring-transcript';

const MAX_MESSAGE_LENGTH = 1024 * 1024;

function TranscriptFeed({ roomId, user, onAccessError }) {
  const [transcript, setTranscript] = useState(emptyTranscript);
  const [status, setStatus] = useState('Connecting…');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const roomRef = useRef(null);
  const scrollRef = useRef(null);
  const followRef = useRef(true);

  useEffect(() => {
    if (!user || !roomId) return;
    const room = new Room();
    roomRef.current = room;
    const controller = new AbortController();
    let disposed = false;
    let tokenTimeout;
    let messages = Promise.resolve();
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      controller.abort();
      clearTimeout(tokenTimeout);
      room.unregisterTextStreamHandler('room');
      room.removeAllListeners();
      void room.disconnect().catch(() => {});
    };
    setTranscript(emptyTranscript());
    setStatus('Connecting…');
    setError('');
    followRef.current = true;

    room.registerTextStreamHandler('room', (reader, participantInfo) => {
      // Only the room agent is authoritative for captions; customers can send other room messages.
      if (disposed || !room.remoteParticipants.get(participantInfo.identity)?.isAgent) return;
      const readMessage = async () => {
        const readController = new AbortController();
        const abort = () => readController.abort();
        controller.signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 10000);
        try {
          let text = '';
          for await (const chunk of reader.withAbortSignal(readController.signal)) {
            text += chunk;
            if (text.length > MAX_MESSAGE_LENGTH) throw new Error('Transcript update too large. Some captions may be missing.');
          }
          return { message: JSON.parse(text) };
        } catch (failure) {
          return { error: failure.message || 'Could not read a transcript update. Some captions may be missing.' };
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener('abort', abort);
        }
      };
      const result = readMessage();
      // Read streams immediately, but apply them in arrival order so a slow read cannot undo a patch.
      messages = messages.then(async () => {
        const next = await result;
        if (disposed) return;
        if (next.error) setError(next.error);
        else setTranscript((previous) => updateTranscript(previous, next.message));
      });
    });
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      if (!disposed) setStatus(state === 'connected' ? 'Live' : 'Reconnecting… · captions may be missed');
    });
    room.on(RoomEvent.Disconnected, () => {
      if (disposed) return;
      setStatus('Disconnected');
      setError('Transcript disconnected. Retry to receive new captions.');
      dispose();
    });

    async function connect() {
      try {
        const token = await user.getIdToken();
        if (disposed) return;
        tokenTimeout = setTimeout(() => controller.abort(), 15000);
        const response = await fetch('/monitoring/api/listen', {
          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId }), signal: controller.signal, cache: 'no-store',
        });
        const body = await response.json().catch(() => null);
        clearTimeout(tokenTimeout);
        if (disposed) return;
        if (!response.ok) {
          const failure = new Error(body?.error || `Transcript access failed (HTTP ${response.status}).`);
          failure.status = response.status;
          throw failure;
        }
        if (!body?.url || !body?.token || body.roomId !== roomId) throw new Error('Transcript access returned an invalid response.');
        await room.connect(body.url, body.token, { autoSubscribe: false });
        if (disposed) { await room.disconnect(); return; }
        setStatus('Live');
      } catch (failure) {
        if (disposed) return;
        dispose();
        setStatus('Disconnected');
        setError(failure.name === 'AbortError' ? 'Transcript access timed out. Please retry.' : failure.message || 'Could not connect to the transcript.');
        if (isAccessError(failure)) onAccessError?.(failure);
      }
    }
    void connect();
    return dispose;
  }, [roomId, user, onAccessError, retry]);

  useLayoutEffect(() => {
    if (followRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  function speaker(turn) {
    const participant = roomRef.current?.remoteParticipants.get(turn.avaId);
    return participant?.attributes?.userName || participant?.attributes?.avaName || participant?.name || turn.avaId;
  }
  function source(turn) {
    const participant = roomRef.current?.remoteParticipants.get(turn.avaId);
    if (turn.sourceKey.startsWith('track-sid:')) return participant?.trackPublications.get(turn.sourceKey.slice(10))?.trackName;
    if (turn.sourceKey.startsWith('track-name:')) return turn.sourceKey.slice(11);
    return '';
  }
  const turns = transcript.segments.flatMap((segment) => segment.turns).filter((turn) => turn.transcript.trim());
  return <div className="monitor-transcript-content">
    <div className="monitor-transcript-status"><span role="status">{status}</span>
      {error && <button className="monitor-secondary" onClick={() => setRetry((value) => value + 1)}>Retry transcript</button>}
    </div>
    <p className="monitor-muted">Captions received while this panel is open. Earlier conversation history is not loaded. Closing the panel clears the feed.</p>
    {error && <p role="alert" className="monitor-error">{error}</p>}
    {transcript.limited && <p className="monitor-note">Showing a limited recent transcript; older or unusually long captions have been trimmed.</p>}
    <div ref={scrollRef} className="monitor-transcript-scroll" role="region" aria-label="Live conversation transcript" tabIndex="0"
      onScroll={(event) => { const area = event.currentTarget; followRef.current = area.scrollHeight - area.scrollTop - area.clientHeight < 40; }}>
      {turns.length ? <ol className="monitor-transcript-turns">{turns.map((turn) => <li key={`${turn.avaId}_${turn.turnId}`}>
        <div className="monitor-transcript-speaker"><strong>{speaker(turn)}</strong>{source(turn) && <span>{source(turn)}</span>}{!turn.isFinal && <span className="monitor-transcript-partial">Speaking…</span>}</div>
        <p>{turn.transcript}</p>
      </li>)}</ol> : <p className="monitor-muted">{status === 'Live' ? 'Waiting for new captions…' : 'No captions received yet.'}</p>}
    </div>
  </div>;
}

export default function LiveKitRoomTranscript(props) {
  const [open, setOpen] = useState(false);
  return <details className="monitor-transcript" onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary>Live transcript</summary>
    {open && <TranscriptFeed {...props} />}
  </details>;
}
